import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CheckCircle2, Loader2, Upload, Send } from "lucide-react";
import { toast } from "sonner";

export default function ServicesSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("checking"); // checking | ready | failed
  const attempts = useRef(0);

  // Onboarding form state
  const [desc, setDesc] = useState("");
  const [phone, setPhone] = useState("");
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let timer;
    const fetchOrder = async () => {
      try {
        if (sessionId) {
          const { data } = await api.get(`/services/status/${sessionId}`);
          if (data.payment_status === "paid" || data.payment_status === "pending_review") {
            const mine = await api.get("/me/services");
            const o = (mine.data || []).find((x) => x.session_id === sessionId || x.id === data.order_id);
            if (o) { setOrder(o); setState("ready"); return; }
          }
          attempts.current += 1;
          if (attempts.current >= 8) { setState("failed"); return; }
          timer = setTimeout(fetchOrder, 2000);
        } else if (orderId) {
          const mine = await api.get("/me/services");
          const o = (mine.data || []).find((x) => x.id === orderId);
          if (o) { setOrder(o); setState("ready"); } else { setState("failed"); }
        } else {
          setState("failed");
        }
      } catch {
        attempts.current += 1;
        if (attempts.current >= 8) { setState("failed"); return; }
        timer = setTimeout(fetchOrder, 2000);
      }
    };
    fetchOrder();
    return () => clearTimeout(timer);
  }, [sessionId, orderId]);

  const submitOnboarding = async (e) => {
    e.preventDefault();
    if (!logo || !desc) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("business_description", desc);
      fd.append("contact_phone", phone);
      fd.append("file", logo);
      await api.post(`/services/${order.id}/onboarding`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSubmitted(true);
      toast.success("Onboarding soumis — notre équipe vous contactera.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="services-success-page">
      <Header />
      <div className="max-w-2xl mx-auto px-5 py-20">
        {state === "checking" && (
          <div className="text-center py-16" data-testid="svc-checking">
            <Loader2 className="h-12 w-12 mx-auto text-[#d4af37] animate-spin mb-5" />
            <div className="font-serif text-2xl">Vérification du paiement…</div>
          </div>
        )}

        {state === "failed" && (
          <div className="text-center py-16" data-testid="svc-failed">
            <div className="font-serif text-3xl mb-3">Commande introuvable</div>
            <p className="text-neutral-400 mb-6">Nous n'avons pas pu localiser votre commande. Contactez l'équipe.</p>
            <Link to="/services" className="btn-outline-gold">Retour aux forfaits</Link>
          </div>
        )}

        {state === "ready" && order && (
          <>
            <div className="text-center mb-10">
              <CheckCircle2 className="h-14 w-14 mx-auto text-[#d4af37] mb-4" />
              <div className="overline">Forfait {order.package} · ${order.amount}</div>
              <h1 className="font-serif text-4xl mt-2">
                {order.payment_status === "paid" ? "Paiement confirmé" : "Commande reçue"}
              </h1>
              <p className="text-neutral-400 mt-3">
                {order.payment_status === "paid"
                  ? "Merci ! Complétez l'onboarding ci-dessous pour que notre équipe démarre la création de votre site."
                  : "Nous examinons votre preuve de paiement. En attendant, vous pouvez déjà téléverser votre logo et description."}
              </p>
            </div>

            {submitted || order.onboarding_status === "submitted" ? (
              <div className="card-imperial p-8 text-center" data-testid="onboarding-done">
                <div className="overline text-[#d4af37] mb-2">✓ Onboarding reçu</div>
                <div className="font-serif text-2xl">Notre équipe vous contactera sous 48 h</div>
                <p className="text-neutral-400 mt-3 text-sm">Vous pouvez suivre l'avancement depuis votre tableau de bord.</p>
                <button onClick={() => nav("/student")} className="btn-outline-gold mt-6">Aller au tableau de bord</button>
              </div>
            ) : (
              <form onSubmit={submitOnboarding} className="card-imperial p-8 space-y-5" data-testid="onboarding-form">
                <div>
                  <div className="overline mb-2">Logo (image)</div>
                  <label htmlFor="logo-file" className="border border-dashed border-[#333] hover:border-[#d4af37] cursor-pointer p-6 flex flex-col items-center text-neutral-400 transition-colors" data-testid="onb-logo-drop">
                    <Upload size={18} className="mb-2 text-[#d4af37]" />
                    <span className="text-sm">{logo ? logo.name : "Téléverser votre logo"}</span>
                  </label>
                  <input id="logo-file" type="file" accept="image/*" className="hidden" onChange={(e) => setLogo(e.target.files?.[0])} data-testid="onb-logo-input" required />
                </div>
                <div>
                  <div className="overline mb-2">Description de l'entreprise</div>
                  <textarea
                    required
                    data-testid="onb-description"
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    rows={5}
                    placeholder="Nom de l'entreprise, secteur d'activité, objectifs du site, pages souhaitées…"
                    className="w-full bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <div className="overline mb-2">Téléphone (optionnel)</div>
                  <input
                    data-testid="onb-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+509 …"
                    className="w-full bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2.5 text-sm"
                  />
                </div>
                <button type="submit" disabled={busy} className="btn-imperial w-full" data-testid="onb-submit">
                  <Send size={14} /> {busy ? "Envoi…" : "Envoyer l'onboarding"}
                </button>
              </form>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
